import { Region } from '@heidi/schema/ml-scribe';
import { useMemo } from 'react';
import { useQueryKindeProperties } from './query/useQueryKindeProperties';

/**
 * Hook to check if the user is in the UK or EU region.
 * Uses the Kinde properties to determine the user's region.
 *
 * `isUKOrEU` folds pending and errored lookups into "in region" so a
 * compliance control prompts rather than waving the action through. Callers
 * that report on that decision need `region` and `isError` too: without them a
 * genuine UK/EU user is indistinguishable from a fail-open guess.
 *
 * @returns isUKOrEU, the raw region, and the query's pending/error state
 */
export function useIsUKOrEU() {
  const { data: kindeProperties, isPending, isError } = useQueryKindeProperties();

  return useMemo(() => {
    const region = kindeProperties?.region;
    const isUKOrEU = isPending || isError || region === Region.UK || region === Region.EU;
    const isEU = isPending || isError || region === Region.EU;

    return {
      isUKOrEU,
      isEU,
      isPending,
      isError,
      region,
    };
  }, [kindeProperties?.region, isPending, isError]);
}
